import { planDisplayName } from '../../../common/planDisplayName';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Stack,
    Chip,
    InputAdornment,
    TextField,
    Tooltip
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { standaloneFieldSx } from '../../../common/fieldStyles';
import { httpClient } from '../../../common/httpclient';
import { messageFromApiErrorBody } from '../../../common/apiErrorMessage';
import { planSegmentLabel } from '../../../common/planSegments';
import DialogForm from "../../../component/dialog/DialogForm";
import PageScaffold from "../../../component/pageScaffold/PageScaffold";
import DefaultTable from "../../../component/table/DefaultTable";
import usePermissions from '../../../hooks/usePermissions';

export default function PlansList() {
    const theme = useTheme();

    const [loading, setLoading] = useState(true);
    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);
    const [search, setSearch] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [data, setData] = useState([]);
    const [planToClone, setPlanToClone] = useState(null);
    const [cloneName, setCloneName] = useState('');
    const [cloning, setCloning] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();

    // Sugere "<nome> (cópia)", com sufixo incremental se já existir na lista.
    const suggestCloneName = (plan) => {
        const names = new Set(data.map(p => p.name));
        const base = `${plan.name} (cópia)`;
        let candidate = base;
        for (let i = 2; names.has(candidate); i++) {
            candidate = `${base} ${i}`;
        }
        return candidate;
    };

    const handleOpenClone = (plan) => {
        setPlanToClone(plan);
        setCloneName(suggestCloneName(plan));
    };

    const handleClonePlan = async () => {
        const name = cloneName.trim();
        if (!name) return;
        setCloning(true);
        try {
            const response = await httpClient.post(`/admin/plans/${planToClone.id}/clone`, { name });
            setPlanToClone(null);
            setSnackbar({ open: true, message: 'Plano clonado com sucesso!', severity: 'success' });
            const newId = response.data?.id;
            if (newId) {
                setTimeout(() => navigate(`/admin/plans/edit/${newId}`), 800);
            } else {
                fetchData();
            }
        } catch (error) {
            const body = error?.response?.data;
            const message = (typeof body === 'string' && body.trim())
                || messageFromApiErrorBody(body)
                || 'Erro ao clonar plano';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setCloning(false);
        }
    };

    const formatCurrency = (valueReais, valueCents) => {
        const totalValue = parseFloat(valueReais || 0) + (parseFloat(valueCents || 0) / 100);
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(totalValue);
    };

    const handleChangeSearch = (searchValue) => {
        setSearch(searchValue);

        if (!searchValue) {
            setFilteredData(data);
            return;
        }

        const searchLower = searchValue.toLowerCase();
        setFilteredData(
            data.filter(plan =>
                plan.name?.toLowerCase().includes(searchLower) ||
                planDisplayName(plan.name)?.toLowerCase().includes(searchLower) ||
                plan.description?.toLowerCase().includes(searchLower) ||
                plan.external_reference?.toLowerCase().includes(searchLower) ||
                formatCurrency(plan.value_reais, plan.value_cents).toLowerCase().includes(searchLower)
            )
        );
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await httpClient.get('/admin/plans');
            // Lista somente os planos visíveis; o endpoint segue retornando todos
            const responseData = (Array.isArray(response.data) ? response.data : [])
                .filter(plan => plan.is_visible);
            setData(responseData);
            setFilteredData(responseData);
        } catch (error) {
            console.error('Error fetching plans:', error);
            setData([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePlan = async () => {
        setLoading(true);
        try {
            await httpClient.delete(`/admin/plans/${planToDelete}`);
            setFilteredData(filteredData.filter(item => item.id !== planToDelete));
            setOpenDialogConfirmDelete(false);
            setPlanToDelete(null);
        } catch (error) {
            console.error('Error deleting plan:', error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            name: 'Nome',
            path: 'name',
            style: { width: '20%', minWidth: '150px' },
            render: (row) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        fontWeight: 500
                    }}
                    title={planDisplayName(row.name)}
                >
                    {planDisplayName(row.name)}
                </Box>
            )
        },
        
        {
            name: 'Valor',
            path: 'value',
            style: { width: '15%', minWidth: '120px' },
            render: (row) => (
                <Box
                    sx={{
                        fontWeight: 600,
                        color: 'success.main',
                        fontSize: '0.9rem'
                    }}
                >
                    {formatCurrency(row.value_reais, row.value_cents)}
                </Box>
            )
        },
        {
            name: 'Segmento',
            path: 'segment',
            style: { width: '12%', minWidth: '130px' },
            render: (row) => (
                <Chip
                    label={planSegmentLabel(row.segment)}
                    color={row.segment ? 'primary' : 'default'}
                    size="small"
                    variant="outlined"
                />
            )
        },
        {
            name: 'Visível',
            path: 'is_visible',
            style: { width: '10%', minWidth: '100px' },
            render: (row) => (
                <Chip
                    icon={row.is_visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    label={row.is_visible ? 'Visível' : 'Oculto'}
                    color={row.is_visible ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                />
            )
        },
        {
            name: 'Catálogo',
            path: 'show_in_catalog',
            style: { width: '12%', minWidth: '110px' },
            render: (row) => (
                <Chip
                    label={row.show_in_catalog ? 'Na landing' : 'Customizado'}
                    color={row.show_in_catalog ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                />
            )
        },
        {
            name: 'Referência Externa',
            path: 'external_reference',
            style: { width: '25%', minWidth: '150px' },
            render: (row) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        color: 'text.secondary',
                        fontSize: '0.875rem'
                    }}
                    title={row.external_reference || ''}
                >
                    {row.external_reference || '-'}
                </Box>
            )
        }
    ];

    const actions = [
        {
            icon: <RemoveRedEyeIcon />,
            props: {
                onClick: (row) => {
                    navigate(`/admin/plans/view/${row.id}`);
                },
                tooltip: 'Visualizar',
                style: { cursor: 'pointer' }
            }
        },
        {
            icon: <EditNoteIcon />,
            props: {
                onClick: (row) => {
                    navigate(`/admin/plans/edit/${row.id}`);
                    console.log('Edit plan:', row.id);
                },
                tooltip: 'Editar',
                style: { cursor: 'pointer' }
            }
        },
        {
            icon: <ContentCopyIcon />,
            props: {
                onClick: handleOpenClone,
                tooltip: 'Clonar',
                style: { cursor: 'pointer' }
            }
        },
        {
            icon: <DeleteIcon color="error" />,
            props: {
                onClick: (row) => {
                    setPlanToDelete(row.id);
                    setOpenDialogConfirmDelete(true);
                },
                tooltip: 'Excluir',
                style: { cursor: 'pointer' }
            }
        },
    ];

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <PageScaffold
            links={[]}
            titlePageTarget={'Planos'}
            openSnackbar={snackbar.open}
            setOpenSnackbar={(open) => setSnackbar(prev => ({ ...prev, open }))}
            message={snackbar.message}
            severity={snackbar.severity}
        >
            {/* Seção de Filtros Moderna */}
            <Card
                sx={{
                    m: 0,
                    borderRadius: 3,
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(0,0,0,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.08)',
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                        : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.05)'
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    {/* Header dos Filtros */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3,
                        gap: 1
                    }}>
                        <FilterListIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: '1.25rem'
                        }}>
                            Filtros de Planos
                        </Typography>
                    </Box>

                    <Box sx={{ width: '100%' }}>
                        {/* Campo de Busca */}
                        <Box sx={{ width: '100%' }}>
                            <TextField
                                id="search-input"
                                placeholder="Pesquisar por nome, descrição, valor ou referência externa..."
                                value={search}
                                onChange={(e) => handleChangeSearch(e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: 'primary.main' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'transparent',
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : 'rgba(0,0,0,0.1)',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme.palette.text.primary,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Ações Rápidas */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 3,
                        pt: 2,
                        borderTop: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.08)'
                            : '1px solid rgba(0,0,0,0.08)'
                    }}>
                        <Stack direction="row" spacing={1}>
                            {search && (
                                <Chip
                                    label={`Busca: "${search}"`}
                                    onDelete={() => handleChangeSearch('')}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : undefined,
                                        color: theme.palette.mode === 'dark'
                                            ? 'primary.light'
                                            : undefined,
                                    }}
                                />
                            )}
                            <Chip
                                label={`${filteredData.length} plano(s) encontrado(s)`}
                                color="info"
                                variant="outlined"
                                size="small"
                                sx={{
                                    borderColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.2)'
                                        : undefined,
                                    color: theme.palette.mode === 'dark'
                                        ? 'info.light'
                                        : undefined,
                                }}
                            />
                        </Stack>

                        <Stack direction="row" spacing={1}>
                            <Tooltip title="Adicionar Plano" placement="top" arrow>
                                <Button
                                    variant="contained"
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={() => navigate('/admin/plans/new')}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 2,
                                        boxShadow: theme.palette.mode === 'dark'
                                            ? '0 4px 12px rgba(0,0,0,0.3)'
                                            : '0 4px 12px rgba(0,0,0,0.15)'
                                    }}
                                >
                                    Adicionar
                                </Button>
                            </Tooltip>
                        </Stack>
                    </Box>
                </CardContent>
            </Card>

            <Box sx={{ width: '100%', mt: 2 }}>
                <DefaultTable
                    colunms={columns}
                    data={filteredData}
                    iconsActions={actions}
                    loading={loading}
                    sortableFields={['name', 'description']}
                    defaultSort={{ field: 'name', direction: 'asc' }}
                />
            </Box>

            <DialogForm
                iconType={'info'}
                open={Boolean(planToClone)}
                title={'Clonar plano'}
                subtitle={planToClone ? `Origem: ${planDisplayName(planToClone.name)}` : ''}
                confirmButtonText={'Clonar'}
                content={'Copia valores, franquias, segmento, módulos e descrições. O clone nasce fora do catálogo da landing, sem referência externa e sem link de pagamento.'}
                onSubmit={handleClonePlan}
                onClose={() => !cloning && setPlanToClone(null)}
                loading={cloning}
                loadingText="Clonando..."
            >
                <TextField
                    fullWidth
                    autoFocus
                    label="Nome do novo plano"
                    value={cloneName}
                    onChange={(e) => setCloneName(e.target.value)}
                    inputProps={{ maxLength: 120 }}
                    error={!cloneName.trim()}
                    helperText={!cloneName.trim() ? 'Nome é obrigatório' : ''}
                    sx={standaloneFieldSx}
                />
            </DialogForm>

            <DialogForm
                iconType={'warning'}
                open={openDialogConfirmDelete}
                title={'Atenção'}
                confirmButtonText={'Sim, Excluir'}
                content={'Tem certeza que deseja excluir este plano?'}
                secondaryContent={'Esta ação não pode ser desfeita. O plano será removido permanentemente do sistema.'}
                onSubmit={() => {
                    handleDeletePlan();
                }}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setPlanToDelete(null);
                }}
                loading={loading}
                loadingText="Removendo..."
            />
        </PageScaffold>
    );
}

